import React, { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  useMediaQuery,
  useTheme,
  TextField,
  MenuItem,
  Divider,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  ExpandMore,
  AccessTime,
  CheckCircle,
  PendingActions,
  PlayArrow,
  Pause,
  Download,
  Upload,
  Rocket,
  GpsFixed,
  Preview,
  Search,
  Analytics,
} from '@mui/icons-material';
import { loadCarrierExport, carrierExportHeaders, carrierTemplateSampleRows, downloadCarrierTemplateCsv } from '../../utils/carrierExport';

/**
 * Advanced Campaign Planner Component
 * Complete workflow: Templates -> Accounts -> Import -> Schedule -> Review
 */
const CampaignPlanner = ({
  plans = [],
  campaigns = [],
  accounts = [],
  accountGroups = [],
  templates = [],
  editingPlan = null,
  onCreatePlan = () => {},
  onEditPlan = () => {},
  onEditComplete = () => {},
  onDeletePlan = () => {},
  onSchedulePlan = () => {},
  onApplySegmentation = () => {},
  onRecipientColumns = () => {},
  onLaunchCampaign = () => {},
  onPauseCampaign = () => {},
  onResumeCampaign = () => {},
  planStats = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // State Management
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  const [failedDialogOpen, setFailedDialogOpen] = useState(false);
  const [failedDialogPlan, setFailedDialogPlan] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingDialogPlan, setTrackingDialogPlan] = useState(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [templatesDialogPlan, setTemplatesDialogPlan] = useState(null);
  const [accountsDialogOpen, setAccountsDialogOpen] = useState(false);
  const [accountsDialogPlan, setAccountsDialogPlan] = useState(null);
  const [filterTab, setFilterTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState('');
  const [recipientFileError, setRecipientFileError] = useState('');
  const [carrierImportNotice, setCarrierImportNotice] = useState('');
  const templateColumns = carrierExportHeaders;

  // Form state for new plan
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedTemplates: [],
    selectedAccounts: [],
    segmentType: 'all',
    recipientGroups: [],
    scheduledDate: '',
    timezone: 'UTC',
    recipientLimit: '',
    sendingFrequency: 'immediate',
    retryCount: 3,
    autoRespond: false,
    trackOpens: true,
    trackClicks: true,
    recipientFileName: '',
    recipientRows: [],
    delayMode: 'fixed',
    delaySeconds: 30,
    minDelaySec: 20,
    maxDelaySec: 60,
    rotateAccounts: true,
    shuffleTemplates: true,
    jitterSec: 5,
    batchPauseEvery: 10,
    batchPauseSec: 120,
    maxPerAccountPerHour: 40,
    maxFailures: 5,
    enforceMinDelay: true,
    // MC Range Auto-Search
    useMcRange: false,
    mcStartNumber: '',
    mcEndNumber: '',
    targetActiveCarriers: true,
    targetInactiveCarriers: false,
    targetBrokers: false,
  });

  const wizardSteps = ['Template', 'Accounts', 'Import', 'Anti-Spam', 'Schedule', 'Review'];

  // Handle editing plan - open wizard with pre-filled data
  useEffect(() => {
    if (editingPlan) {
      // Pre-fill form with editing plan data
      setFormData({
        name: editingPlan.name || '',
        description: editingPlan.description || '',
        selectedTemplates: editingPlan.strategy?.selectedTemplates || [],
        selectedAccounts: editingPlan.strategy?.selectedAccounts || [],
        segmentType: editingPlan.segmentType || 'all',
        recipientGroups: editingPlan.recipientGroups || [],
        scheduledDate: editingPlan.scheduledDate || '',
        timezone: editingPlan.timezone || 'UTC',
        recipientLimit: editingPlan.recipientLimit || '',
        sendingFrequency: editingPlan.sendingFrequency || 'immediate',
        retryCount: editingPlan.retryCount || 3,
        autoRespond: editingPlan.autoRespond || false,
        trackOpens: editingPlan.strategy?.trackOpens !== false,
        trackClicks: editingPlan.strategy?.trackClicks !== false,
        recipientFileName: editingPlan.recipientFileName || '',
        recipientRows: editingPlan.recipientRows || [],
        delayMode: editingPlan.strategy?.delayMode || 'fixed',
        delaySeconds: editingPlan.strategy?.delaySeconds || 30,
        minDelaySec: editingPlan.strategy?.minDelaySec || 20,
        maxDelaySec: editingPlan.strategy?.maxDelaySec || 60,
        rotateAccounts: editingPlan.strategy?.rotateAccounts !== false,
        shuffleTemplates: editingPlan.strategy?.shuffleTemplates !== false,
        jitterSec: editingPlan.strategy?.jitterSec || 5,
        batchPauseEvery: editingPlan.strategy?.batchPauseEvery || 10,
        batchPauseSec: editingPlan.strategy?.batchPauseSec || 120,
        maxPerAccountPerHour: editingPlan.strategy?.maxPerAccountPerHour || 40,
        maxFailures: editingPlan.strategy?.maxFailures || 5,
        enforceMinDelay: editingPlan.strategy?.enforceMinDelay !== false,
        useMcRange: editingPlan.useMcRange || false,
        mcStartNumber: editingPlan.mcStartNumber || '',
        mcEndNumber: editingPlan.mcEndNumber || '',
        targetActiveCarriers: editingPlan.targetActiveCarriers !== false,
        targetInactiveCarriers: editingPlan.targetInactiveCarriers || false,
        targetBrokers: editingPlan.targetBrokers || false,
      });
      // Open wizard
      setWizardOpen(true);
      setWizardStep(0);
    }
  }, [editingPlan]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#2196F3';
      case 'running': return '#4CAF50';
      case 'completed': return '#FF9800';
      case 'draft': return '#667eea';
      case 'paused': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <AccessTime />;
      case 'running': return <PlayArrow />;
      case 'completed': return <CheckCircle />;
      case 'draft': return <PendingActions />;
      case 'paused': return <Pause />;
      default: return <MoreVert />;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not scheduled';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const countRecipientsFromPlan = (plan) => {
    if (plan?.useMcRange) {
      const start = Number(plan?.mcStartNumber || 0);
      const end = Number(plan?.mcEndNumber || 0);
      if (start > 0 && end >= start) {
        return end - start + 1;
      }
    }
    const rows = Array.isArray(plan?.recipientRows)
      ? plan.recipientRows
      : (Array.isArray(plan?.recipients) ? plan.recipients : []);
    if (!rows.length) return 0;
    let count = 0;
    rows.forEach((row) => {
      if (!row) return;
      if (Array.isArray(row)) {
        row.forEach((cell) => {
          const email = String(cell || '').trim();
          if (email.includes('@')) count += 1;
        });
        return;
      }
      if (typeof row === 'string') {
        String(row)
          .split(/[\n,;]+/)
          .map((entry) => entry.trim())
          .filter((entry) => entry.includes('@'))
          .forEach(() => {
            count += 1;
          });
        return;
      }
      if (typeof row !== 'object') return;
      const keys = Object.keys(row);
      const emailKey =
        keys.find((key) => /email/i.test(key)) ||
        keys.find((key) => /e-mail/i.test(key)) ||
        keys.find((key) => /mail/i.test(key));
      if (!emailKey) {
        Object.values(row).forEach((cell) => {
          const email = String(cell || '').trim();
          if (email.includes('@')) count += 1;
        });
        return;
      }
      const value = row[emailKey];
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          const email = String(entry || '').trim();
          if (email.includes('@')) count += 1;
        });
        return;
      }
      String(value || '')
        .split(/[\n,;]+/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.includes('@'))
        .forEach(() => {
          count += 1;
        });
    });
    return count || rows.length;
  };

  const downloadPlanRecipients = (plan) => {
    const rows = Array.isArray(plan?.recipientRows)
      ? plan.recipientRows
      : (Array.isArray(plan?.recipients) ? plan.recipients : []);
    if (!rows.length) return;
    const firstRow = rows[0];
    const headers = Array.isArray(firstRow)
      ? firstRow.map((_, idx) => `Column ${idx + 1}`)
      : Object.keys(firstRow || {});
    if (!headers.length) return;
    const csvLines = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((key) => {
            const value = Array.isArray(row)
              ? row[Number(key.replace('Column ', '')) - 1]
              : row?.[key] ?? '';
            const cell = String(value).replace(/"/g, '""');
            return `"${cell}"`;
          })
          .join(',')
      ),
    ].join('\n');
    const safeName = String(plan?.name || 'campaign').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
    const filename = `${safeName || 'campaign'}_recipients.csv`;
    const blob = new Blob([`${csvLines}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleWizardNext = () => {
    if (wizardStep < wizardSteps.length - 1) {
      setWizardStep(wizardStep + 1);
    }
  };

  const handleWizardPrev = () => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1);
    }
  };

  const handleHeaderAction = (action) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  const handleCreatePlan = () => {
    const delayMode = formData.delayMode === 'range' ? 'range' : 'fixed';
    const fixedDelay = Math.max(0, Number(formData.delaySeconds) || 0);
    const minDelay = Math.max(0, Number(formData.minDelaySec) || 0);
    const maxDelay = Math.max(minDelay, Number(formData.maxDelaySec) || 0);
    const jitterSec = Math.max(0, Number(formData.jitterSec) || 0);
    const batchPauseEvery = Math.max(0, Number(formData.batchPauseEvery) || 0);
    const batchPauseSec = Math.max(0, Number(formData.batchPauseSec) || 0);
    const maxPerAccountPerHour = Math.max(0, Number(formData.maxPerAccountPerHour) || 0);
    const maxFailures = Math.max(0, Number(formData.maxFailures) || 0);

    const planData = {
      name: formData.name,
      description: formData.description,
      selectedTemplates: formData.selectedTemplates || [],
      selectedAccounts: formData.selectedAccounts || [],
      segmentType: formData.segmentType,
      recipientGroups: formData.recipientGroups || [],
      scheduledDate: formData.scheduledDate,
      timezone: formData.timezone,
      recipientLimit: formData.recipientLimit,
      sendingFrequency: formData.sendingFrequency,
      retryCount: formData.retryCount,
      autoRespond: formData.autoRespond,
      trackOpens: formData.trackOpens,
      trackClicks: formData.trackClicks,
      recipientFileName: formData.recipientFileName || '',
      recipientRows: formData.recipientRows || [],
      minDelaySec: delayMode === 'range' ? minDelay : fixedDelay,
      maxDelaySec: delayMode === 'range' ? maxDelay : fixedDelay,
      rotateAccounts: formData.rotateAccounts !== false,
      shuffleTemplates: formData.shuffleTemplates !== false,
      jitterSec,
      batchPauseEvery,
      batchPauseSec,
      maxPerAccountPerHour,
      maxFailures,
      enforceMinDelay: formData.enforceMinDelay !== false,
      // MC Range Auto-Search
      useMcRange: formData.useMcRange,
      mcStartNumber: formData.mcStartNumber,
      mcEndNumber: formData.mcEndNumber,
      targetActiveCarriers: formData.targetActiveCarriers,
      targetInactiveCarriers: formData.targetInactiveCarriers,
      targetBrokers: formData.targetBrokers,
    };

    // Check if editing or creating
    if (editingPlan) {
      // Editing existing campaign
      onEditComplete(planData);
    } else {
      // Creating new campaign
      onCreatePlan(planData);
    }

    // Reset form
    setFormData({
      name: '',
      description: '',
      selectedTemplates: [],
      selectedAccounts: [],
      segmentType: 'all',
      recipientGroups: [],
      scheduledDate: '',
      timezone: 'UTC',
      recipientLimit: '',
      sendingFrequency: 'immediate',
      retryCount: 3,
      autoRespond: false,
      trackOpens: true,
      trackClicks: true,
      recipientFileName: '',
      recipientRows: [],
      delayMode: 'fixed',
      delaySeconds: 30,
      minDelaySec: 20,
      maxDelaySec: 60,
      rotateAccounts: true,
      shuffleTemplates: true,
      jitterSec: 5,
      batchPauseEvery: 10,
      batchPauseSec: 120,
      maxPerAccountPerHour: 40,
      maxFailures: 5,
      enforceMinDelay: true,
      useMcRange: false,
      mcStartNumber: '',
      mcEndNumber: '',
      targetActiveCarriers: true,
      targetInactiveCarriers: false,
      targetBrokers: false,
    });
    setWizardStep(0);
    setWizardOpen(false);
  };

  // Filter plans based on tab
  const filteredPlans = plans.filter((plan) => {
    const statusMap = ['', 'draft', 'scheduled', 'running', 'completed'];
    const matchesTab = filterTab === 0 || plan.status === statusMap[filterTab];
    const matchesSearch = plan.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const statusCounts = plans.reduce(
    (acc, plan) => {
      acc.all += 1;
      const status = plan?.status || 'draft';
      if (!acc[status]) acc[status] = 0;
      acc[status] += 1;
      return acc;
    },
    { all: 0, draft: 0, scheduled: 0, running: 0, completed: 0, paused: 0, failed: 0 }
  );

  const renderTabLabel = (label, count, icon, color) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: 999,
          bgcolor: color,
          color: '#fff',
          fontSize: '0.7rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {count}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="caption" fontWeight={700}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  const filteredAccounts = accounts.filter((account) => {
    if (!accountSearch) return true;
    const hay = `${account.label || ''} ${account.fromEmail || ''} ${account.username || ''}`.toLowerCase();
    return hay.includes(accountSearch.toLowerCase());
  });

  const selectedStats = selectedPlan ? (planStats[selectedPlan.id] || {}) : {};
  const previewTemplate = selectedPlan
    ? (selectedPlan.templates && selectedPlan.templates.length
      ? selectedPlan.templates[0]
      : templates.find((t) => t.id === selectedPlan?.strategy?.selectedTemplates?.[0]))
    : null;

  const addAccountsToSelection = (ids) => {
    const next = Array.from(new Set([...(formData.selectedAccounts || []), ...(ids || [])]));
    setFormData({ ...formData, selectedAccounts: next });
  };

  const handleRecipientFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setRecipientFileError('');
    setCarrierImportNotice('');
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (!rows.length) {
        throw new Error('No rows found in the uploaded file.');
      }
      const headers = (rows[0] || []).map((h, idx) => {
        const label = String(h || '').trim();
        return label || `Column ${idx + 1}`;
      });
      const dataRows = rows.slice(1).filter((row) => row.some((cell) => String(cell || '').trim() !== ''));
      const mappedRows = dataRows.map((row) => {
        const record = {};
        headers.forEach((key, idx) => {
          record[key] = row[idx] ?? '';
        });
        return record;
      });
      setFormData((prev) => ({
        ...prev,
        recipientFileName: file.name,
        recipientRows: mappedRows,
      }));
      onRecipientColumns(headers);
    } catch (err) {
      setRecipientFileError(err.message || 'Failed to read file');
      setFormData((prev) => ({ ...prev, recipientFileName: '', recipientRows: [] }));
    } finally {
      event.target.value = '';
    }
  };

  const handleLoadCarrierExport = () => {
    const record = loadCarrierExport();
    if (!record || !record.rows?.length) {
      setRecipientFileError('No carrier search export found. Please run a carrier search first.');
      setCarrierImportNotice('');
      return;
    }
    const headers = record.headers || Object.keys(record.rows[0] || {});
    const emailKey =
      headers.find((key) => /email/i.test(key)) ||
      headers.find((key) => /e-mail/i.test(key)) ||
      headers.find((key) => /mail/i.test(key));
    const emailCount = record.rows.reduce((count, row) => {
      const value = emailKey ? row[emailKey] : row.Email || row.email || '';
      return value ? count + 1 : count;
    }, 0);
    if (!emailKey || !emailCount) {
      setRecipientFileError('Carrier export loaded, but no email column was found.');
      setCarrierImportNotice('');
      return;
    }
    setRecipientFileError('');
    setFormData((prev) => ({
      ...prev,
      recipientFileName: `Carrier Search (${new Date(record.createdAt).toLocaleString()})`,
      recipientRows: record.rows,
    }));
    onRecipientColumns(headers);
    setCarrierImportNotice(`Loaded ${record.count} carriers (${emailCount} with email).`);
  };

  const downloadRecipientTemplate = () => {
    downloadCarrierTemplateCsv({ filename: 'campaign_recipients_template.csv', rows: carrierTemplateSampleRows });
  };

  const importRecipientCount = Array.isArray(formData.recipientRows) ? formData.recipientRows.length : 0;
  const importAllKeys = importRecipientCount > 0 ? Object.keys(formData.recipientRows[0] || {}) : [];
  const importPreviewKeys = importAllKeys.slice(0, 5);
  const importEmailKey = importAllKeys.find((key) => /email|e-mail|mail/i.test(key)) || '';
  const importRowsWithEmail = importRecipientCount > 0
    ? formData.recipientRows.reduce((count, row) => (
        importEmailKey && String(row?.[importEmailKey] || '').trim() ? count + 1 : count
      ), 0)
    : 0;
  const reviewDelayLabel = formData.delayMode === 'range'
    ? `${formData.minDelaySec || 0}-${formData.maxDelaySec || 0}s`
    : `${formData.delaySeconds || 0}s`;
  const reviewScheduleLabel = formData.scheduledDate ? formatDate(formData.scheduledDate) : 'Not scheduled';
  const reviewSummaryCards = [
    {
      label: 'Templates',
      value: formData.selectedTemplates?.length || 0,
      tone: 'rgba(14,165,233,0.10)',
      border: 'rgba(14,165,233,0.22)'
    },
    {
      label: 'Accounts',
      value: formData.selectedAccounts?.length || 0,
      tone: 'rgba(34,197,94,0.10)',
      border: 'rgba(34,197,94,0.22)'
    },
    {
      label: 'Recipients',
      value: formData.useMcRange
        ? `${Math.max(0, (Number(formData.mcEndNumber) || 0) - (Number(formData.mcStartNumber) || 0) + 1)} MC`
        : (formData.recipientRows?.length || 0),
      tone: 'rgba(168,85,247,0.10)',
      border: 'rgba(168,85,247,0.22)'
    },
    {
      label: 'With Email',
      value: importRowsWithEmail || 0,
      tone: 'rgba(245,158,11,0.12)',
      border: 'rgba(245,158,11,0.24)'
    }
  ];
  const reviewDeliveryItems = [
    { label: 'Delay', value: reviewDelayLabel },
    { label: 'Jitter', value: `${formData.jitterSec || 0}s` },
    { label: 'Max / hour', value: `${formData.maxPerAccountPerHour || 0}` },
    { label: 'Pause rule', value: `${formData.batchPauseEvery || 0} emails / ${formData.batchPauseSec || 0}s` },
    { label: 'Stop after failures', value: `${formData.maxFailures || 0}` },
    { label: 'Timezone', value: formData.timezone || 'UTC' }
  ];
  const reviewBehaviorItems = [
    { label: 'Rotate accounts', value: formData.rotateAccounts ? 'Enabled' : 'Disabled' },
    { label: 'Shuffle templates', value: formData.shuffleTemplates ? 'Enabled' : 'Disabled' },
    { label: 'Track opens', value: formData.trackOpens ? 'Enabled' : 'Disabled' },
    { label: 'Track clicks', value: formData.trackClicks ? 'Enabled' : 'Disabled' }
  ];
  const reviewChecks = [
    {
      label: 'Campaign identity',
      helper: formData.name ? formData.name : 'Add a campaign name',
      ok: Boolean(formData.name)
    },
    {
      label: 'Template selection',
      helper: `${formData.selectedTemplates?.length || 0} template selected`,
      ok: (formData.selectedTemplates?.length || 0) > 0
    },
    {
      label: 'Sending accounts',
      helper: `${formData.selectedAccounts?.length || 0} account selected`,
      ok: (formData.selectedAccounts?.length || 0) > 0
    },
    {
      label: formData.useMcRange ? 'MC range' : 'Recipients file',
      helper: formData.useMcRange
        ? ((formData.mcStartNumber && formData.mcEndNumber)
          ? `MC-${formData.mcStartNumber} to MC-${formData.mcEndNumber}`
          : 'Enter MC range')
        : (formData.recipientFileName || 'No file imported'),
      ok: formData.useMcRange
        ? Boolean(formData.mcStartNumber && formData.mcEndNumber)
        : (formData.recipientRows?.length || 0) > 0
    },
    {
      label: 'Schedule',
      helper: reviewScheduleLabel,
      ok: Boolean(formData.scheduledDate)
    }
  ];
  const reviewReadyCount = reviewChecks.filter((item) => item.ok).length;
  const reviewNotReady = reviewChecks.filter((item) => !item.ok);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* ====== HEADER SECTION ====== */}
      <Card
        variant="outlined"
        sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
          borderLeft: '4px solid #667eea',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Rocket sx={{ fontSize: 28, color: '#667eea' }} />
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800}>
                  Campaign Planner Pro
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Template {'->'} Accounts {'->'} Import {'->'} Anti-Spam {'->'} Schedule {'->'} Review
              </Typography>
            </Box>
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              startIcon={<Add />}
              onClick={() => setWizardOpen(true)}
              fullWidth={isMobile}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {isMobile ? 'New' : 'Create Campaign'}
            </Button>
          </Stack>

          {/* Dashboard Stats */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">Total Plans</Typography>
                <Typography fontWeight={700} variant="h6">{plans.length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#e3f2fd' }}>
                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                <Typography fontWeight={700} variant="h6">{plans.filter((p) => p.status === 'scheduled').length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#e8f5e9' }}>
                <Typography variant="caption" color="text.secondary">Running</Typography>
                <Typography fontWeight={700} variant="h6">{plans.filter((p) => p.status === 'running').length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, textAlign: 'center', background: '#fff3e0' }}>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography fontWeight={700} variant="h6">{plans.filter((p) => p.status === 'completed').length}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ====== FILTER & SEARCH ====== */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          placeholder="Search campaigns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          fullWidth={isMobile}
          sx={{ flex: 1 }}
        />
        <Tabs
          value={filterTab}
          onChange={(e, newValue) => setFilterTab(newValue)}
          sx={{ borderBottom: '1px solid #eee', overflow: 'auto' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={renderTabLabel('All', statusCounts.all, <MoreVert fontSize="small" />, '#6c63ff')} />
          <Tab label={renderTabLabel('Draft', statusCounts.draft, <PendingActions fontSize="small" />, '#ff9800')} />
          <Tab label={renderTabLabel('Scheduled', statusCounts.scheduled, <AccessTime fontSize="small" />, '#2196f3')} />
          <Tab label={renderTabLabel('Running', statusCounts.running, <PlayArrow fontSize="small" />, '#4caf50')} />
          <Tab label={renderTabLabel('Completed', statusCounts.completed, <CheckCircle fontSize="small" />, '#8e24aa')} />
        </Tabs>
      </Stack>

      {/* ====== PLANS LIST ====== */}
      {filteredPlans.length === 0 ? (
        <Alert
          severity="info"
          icon={<GpsFixed />}
          sx={{
            background: 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)',
          }}
        >
          <Typography fontWeight={700}>No campaigns found</Typography>
          <Typography variant="caption">Create a new campaign to get started</Typography>
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {filteredPlans.map((plan) => {
            const stats = planStats[plan.id] || {
              totalRecipients: 0,
              processed: 0,
              sent: 0,
              failed: 0,
              opens: 0,
              clicks: 0,
              conversions: 0,
              estimatedEngagement: 0,
              campaignsIncluded: 0,
              accountsUsed: 0,
              lastMessage: '',
              events: [],
              discovery: null,
            };
            const derivedRecipients = stats.totalRecipients || countRecipientsFromPlan(plan);
            const derivedSegment = plan.segmentType || plan.strategy?.segmentType || 'All';
            const engagementRate = stats.estimatedEngagement
              || (stats.totalRecipients
                ? Math.round(((stats.opens || 0) / Math.max(stats.totalRecipients, 1)) * 100)
                : 0);
            const selectedTemplateIds = plan.strategy?.selectedTemplates || plan.selectedTemplates || [];
            const selectedAccountIds = plan.strategy?.selectedAccounts || plan.selectedAccounts || [];
            const planTemplates = (plan.templates && plan.templates.length)
              ? plan.templates
              : templates.filter((tmpl) => selectedTemplateIds.includes(tmpl.id));
            const planAccounts = accounts.filter((acc) => selectedAccountIds.includes(acc.id));

            return (
              <Accordion key={plan.id} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%', pr: 2 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                      <Box sx={{
                        p: 1,
                        borderRadius: 1,
                        background: `${getStatusColor(plan.status)}20`,
                        display: 'flex',
                      }}>
                        {getStatusIcon(plan.status)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={700} variant="body2">
                          {plan.name || 'Unnamed Campaign'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={plan.status?.toUpperCase()}
                            sx={{
                              backgroundColor: getStatusColor(plan.status) + '30',
                              color: getStatusColor(plan.status),
                              fontWeight: 700,
                            }}
                          />
                          {selectedTemplateIds.length > 0 && (
                            <Chip
                              size="small"
                              label={`${selectedTemplateIds.length} templates`}
                              variant="outlined"
                              onClick={handleHeaderAction(() => {
                                setTemplatesDialogPlan(plan);
                                setTemplatesDialogOpen(true);
                              })}
                            />
                          )}
                          {selectedAccountIds.length > 0 && (
                            <Chip
                              size="small"
                              label={`${selectedAccountIds.length} accounts`}
                              onClick={handleHeaderAction(() => {
                                setAccountsDialogPlan(plan);
                                setAccountsDialogOpen(true);
                              })}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                    {!isMobile && engagementRate ? (
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#4CAF50' }}>
                        ~{Math.round(engagementRate || 0)}% engagement
                      </Typography>
                    ) : null}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent="flex-end"
                      sx={{ rowGap: 1 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Preview />}
                        onClick={handleHeaderAction(() => {
                          setSelectedPlan(plan);
                          setPreviewDialog(true);
                        })}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Analytics />}
                        onClick={handleHeaderAction(() => {
                          setSelectedPlan(plan);
                          setAnalyticsDialog(true);
                        })}
                      >
                        Analytics
                      </Button>
                      {(plan.status === 'draft' || plan.status === 'completed' || plan.status === 'failed' || plan.status === 'paused') && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Rocket />}
                          onClick={handleHeaderAction(() => onLaunchCampaign(plan.id))}
                          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                        >
                          {plan.status === 'draft' ? 'Launch' : 'Run again'}
                        </Button>
                      )}
                      {plan.status === 'running' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Pause />}
                          onClick={handleHeaderAction(() => onPauseCampaign(plan.id))}
                        >
                          Pause
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={handleHeaderAction(() => onEditPlan(plan))}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleHeaderAction(() => onDeletePlan(plan.id))}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Stack spacing={2}>
                    {/* Stats Grid */}
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#f5f5f5', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Recipients</Typography>
                          <Typography fontWeight={700}>{derivedRecipients}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#e3f2fd', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Opens</Typography>
                          <Typography fontWeight={700}>{stats.opens || 0}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#e8f5e9', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Clicks</Typography>
                          <Typography fontWeight={700}>{stats.clicks || 0}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, background: '#fff3e0', textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Conversions</Typography>
                          <Typography fontWeight={700}>{stats.conversions || 0}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {stats.totalRecipients && (stats.sent || stats.failed) ? (
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Sending progress
                          </Typography>
                          <Typography variant="caption">
                            {stats.sent || 0}/{stats.totalRecipients}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            100,
                            ((stats.sent || 0) / Math.max(stats.totalRecipients || 1, 1)) * 100
                          )}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                        {stats.failed ? (
                          <Typography variant="caption" color="error">
                            Failed: {stats.failed}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}

                    {stats.lastMessage ? (
                      <Alert severity={plan.status === 'failed' ? 'error' : (plan.status === 'completed' ? 'success' : 'info')}>
                        {stats.lastMessage}
                      </Alert>
                    ) : null}

                    {stats.failedRecipients && stats.failedRecipients.length ? (
                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={handleHeaderAction(() => {
                            setFailedDialogPlan(plan);
                            setFailedDialogOpen(true);
                          })}
                        >
                          Failed recipients ({stats.failedRecipients.length})
                        </Button>
                      </Box>
                    ) : null}
                    {stats.trackingWarnings && stats.trackingWarnings.length ? (
                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={handleHeaderAction(() => {
                            setTrackingDialogPlan(plan);
                            setTrackingDialogOpen(true);
                          })}
                        >
                          Tracking issues ({stats.trackingWarnings.length})
                        </Button>
                      </Box>
                    ) : null}

                    <Divider />

                    {/* Campaign Details */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography fontWeight={700} variant="body2" mb={1}>Campaign Info</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>Templates: <strong>{(plan.strategy?.selectedTemplates || plan.selectedTemplates || []).length || 0}</strong></Typography>
                                <Typography>Accounts: <strong>{(plan.strategy?.selectedAccounts || plan.selectedAccounts || []).length || 0}</strong></Typography>
                                <Typography>Segment: <strong>{derivedSegment}</strong></Typography>
                                {plan.useMcRange ? (
                                  <Typography>
                                    MC Range: <strong>MC-{plan.mcStartNumber || '?'} to MC-{plan.mcEndNumber || '?'}</strong>
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>Recipients: <strong>{derivedRecipients}</strong></Typography>
                                <Typography>Processed: <strong>{stats.processed || 0}</strong></Typography>
                                <Typography>Sent: <strong>{stats.sent || 0}</strong></Typography>
                                <Typography>Failed: <strong>{stats.failed || 0}</strong></Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Typography>Recipients file:</Typography>
                                  {plan.recipientFileName ? (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={handleHeaderAction(() => downloadPlanRecipients(plan))}
                                    >
                                      {plan.recipientFileName}
                                    </Button>
                                  ) : (
                                    <Typography component="span"><strong>None</strong></Typography>
                                  )}
                                </Stack>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography fontWeight={700} variant="body2" mb={1}>Schedule Info</Typography>
                          <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                            <Typography>Scheduled: <strong>{formatDate(plan.scheduledDate)}</strong></Typography>
                            <Typography>Timezone: <strong>{plan.timezone || 'UTC'}</strong></Typography>
                            <Typography>Frequency: <strong>{plan.sendingFrequency || 'Immediate'}</strong></Typography>
                            <Typography>Last run: <strong>{stats.lastRunAt || 'Not started'}</strong></Typography>
                          </Stack>
                        </Box>
                      </Grid>
                      {(stats.discovery || (stats.events && stats.events.length > 0)) && (
                        <Grid item xs={12}>
                          <Box>
                            <Typography fontWeight={700} variant="body2" mb={1}>Run Summary</Typography>
                            <Paper sx={{ p: 1.5, background: '#f8fafc' }}>
                              <Stack spacing={0.75}>
                                {stats.discovery ? (
                                  <Typography variant="body2">
                                    MC discovery: checked <strong>{stats.discovery.searchedMcCount || 0}</strong>, matched <strong>{stats.discovery.matchedCarrierCount || 0}</strong>, usable recipients <strong>{stats.discovery.uniqueRecipients || 0}</strong>, skipped missing <strong>{stats.discovery.skippedMissingMc || 0}</strong>
                                  </Typography>
                                ) : null}
                                {(stats.events || []).slice(-5).reverse().map((event, idx) => (
                                  <Typography key={`${event.at || 'event'}-${idx}`} variant="caption" color="text.secondary">
                                    [{event.at || '-'}] {event.message || ''}
                                  </Typography>
                                ))}
                              </Stack>
                            </Paper>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Box>
                          <Typography fontWeight={700} variant="body2" mb={1}>Anti-Spam Settings</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>
                                  Delay: <strong>{Number(plan.strategy?.minDelaySec || 0)}-{Number(plan.strategy?.maxDelaySec || 0)}s</strong>
                                </Typography>
                                <Typography>Jitter: <strong>{Number(plan.strategy?.jitterSec || 0)}s</strong></Typography>
                                <Typography>
                                  Rotate accounts: <strong>{plan.strategy?.rotateAccounts ? 'Yes' : 'No'}</strong>
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={0.5} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <Typography>
                                  Shuffle templates: <strong>{plan.strategy?.shuffleTemplates ? 'Yes' : 'No'}</strong>
                                </Typography>
                                <Typography>
                                  Max/hour: <strong>{Number(plan.strategy?.maxPerAccountPerHour || 0)}</strong>
                                </Typography>
                                <Typography>
                                  Pause: <strong>{Number(plan.strategy?.batchPauseEvery || 0)} emails / {Number(plan.strategy?.batchPauseSec || 0)}s</strong>
                                </Typography>
                                <Typography>
                                  Stop after failures: <strong>{Number(plan.strategy?.maxFailures || 0)}</strong>
                                </Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Performance Chart */}
                    <Box>
                      <Typography fontWeight={700} variant="body2" mb={1}>Performance</Typography>
                      <Box sx={{
                        p: 1.5,
                        background: '#f5f5f5',
                        borderRadius: 1,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                        gap: 1,
                      }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Open Rate</Typography>
                          <Typography fontWeight={700}>{Math.round((stats.opens / (stats.totalRecipients || 1) * 100)) || 0}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Click Rate</Typography>
                          <Typography fontWeight={700}>{Math.round((stats.clicks / (stats.opens || 1) * 100)) || 0}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Conversion</Typography>
                          <Typography fontWeight={700}>{Math.round((stats.conversions / (stats.clicks || 1) * 100)) || 0}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Engagement</Typography>
                          <Typography fontWeight={700} sx={{ color: '#4CAF50' }}>{Math.round(engagementRate || 0)}%</Typography>
                        </Box>
                      </Box>
                    </Box>

                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      {/* ====== CREATE CAMPAIGN WIZARD ====== */}
      <Dialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 4,
            minHeight: { xs: '92vh', md: '94vh' },
            maxHeight: { xs: '92vh', md: '94vh' },
            overflow: 'hidden',
          }
        }}
      >
        {wizardStep === 0 && (
          <DialogTitle sx={{ p: 0 }}>
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                p: { xs: 2.5, sm: 3 },
                color: '#f8fafc',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 35%, #0ea5e9 100%)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -40,
                  right: -60,
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 70%)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -70,
                  left: -30,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(14,165,233,0.4) 0%, rgba(14,165,233,0) 70%)',
                }}
              />
              <Stack spacing={1} sx={{ position: 'relative', zIndex: 1 }}>
                <Chip
                  label="Campaign Wizard"
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: '#e2e8f0',
                    fontWeight: 700,
                    letterSpacing: 0.4,
                  }}
                />
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{ fontWeight: 800, fontFamily: "Georgia, 'Times New Roman', serif" }}
                >
                  {editingPlan ? 'Edit Campaign' : 'Create New Campaign'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.8)', maxWidth: 560 }}>
                  {editingPlan 
                    ? 'Update campaign settings, templates, accounts and scheduling.'
                    : 'Design a high-performing campaign with smart routing, anti-spam controls, and schedule precision.'
                  }
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label="Template Ready" size="small" sx={{ bgcolor: 'rgba(15,118,110,0.35)', color: '#ecfeff' }} />
                  <Chip label="Smart Scheduling" size="small" sx={{ bgcolor: 'rgba(59,130,246,0.35)', color: '#eff6ff' }} />
                  <Chip label="Anti-Spam Guard" size="small" sx={{ bgcolor: 'rgba(234,88,12,0.35)', color: '#fff7ed' }} />
                </Stack>
              </Stack>
            </Box>
          </DialogTitle>
        )}
        <DialogContent
          sx={{
            pt: wizardStep === 0 ? 3 : 1.5,
            pb: 3,
            px: { xs: 2, sm: 3 },
            overflowY: 'auto'
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              mb: 3,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(15,23,42,0.04) 0%, rgba(14,165,233,0.08) 100%)',
              position: 'sticky',
              top: wizardStep === 0 ? 0 : 8,
              zIndex: 2,
              backdropFilter: 'blur(12px)',
            }}
          >
            <Stepper activeStep={wizardStep} alternativeLabel={!isMobile}>
              {wizardSteps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontWeight: 700,
                        color: 'text.primary',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          {/* Step Content */}
          {wizardStep === 0 && (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(15,23,42,0.02) 0%, rgba(14,165,233,0.06) 100%)',
                }}
              >
                <Stack spacing={1.5}>
                  <Stack spacing={0.5}>
                    <Typography fontWeight={800} variant="body1">
                      Campaign Identity
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Give the campaign a clear name and purpose so teams can track it easily.
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Campaign Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Spring Sale 2024"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Short goal or offer summary"
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Stack spacing={1.5}>
                  <Stack spacing={0.5}>
                    <Typography fontWeight={800} variant="body1">
                      Template Selection
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose one or more templates. We will rotate or shuffle them based on your anti-spam settings.
                    </Typography>
                  </Stack>
                  <FormControl fullWidth>
                    <InputLabel>Select Templates</InputLabel>
                    <Select
                      multiple
                      value={formData.selectedTemplates}
                      onChange={(e) => setFormData({ ...formData, selectedTemplates: e.target.value })}
                      label="Select Templates"
                      renderValue={(selected) => (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(selected || []).map((id) => (
                            <Chip
                              key={id}
                              label={templates.find((t) => t.id === id)?.name || id}
                              size="small"
                              sx={{ bgcolor: 'rgba(14,165,233,0.12)', fontWeight: 700 }}
                            />
                          ))}
                        </Stack>
                      )}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 320,
                          },
                        },
                      }}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          <Stack>
                            <Typography fontWeight={700} variant="body2">
                              {t.name}
                            </Typography>
                            {t.subject ? (
                              <Typography variant="caption" color="text.secondary">
                                {t.subject}
                              </Typography>
                            ) : null}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`${formData.selectedTemplates?.length || 0} selected`} size="small" />
                    <Chip label="Use Shuffle for variety" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.12)' }} />
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          )}

          {wizardStep === 1 && (
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography fontWeight={800} variant="body1">Select Sending Accounts</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pick the accounts that will deliver this campaign. You can add whole groups or choose individually.
                </Typography>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <TextField
                  size="small"
                  placeholder="Search accounts..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 360 }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`${formData.selectedAccounts?.length || 0} selected`}
                    size="small"
                    sx={{ bgcolor: 'rgba(15,23,42,0.08)' }}
                  />
                  <Chip
                    label={`${accounts.length} total`}
                    size="small"
                    sx={{ bgcolor: 'rgba(14,165,233,0.12)' }}
                  />
                </Stack>
              </Stack>
              {accountGroups.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">Account Groups</Typography>
                  {accountGroups.map((group) => {
                    const groupIds = group.accountIds || [];
                    const inSelection = groupIds.every((id) =>
                      (formData.selectedAccounts || []).includes(id)
                    );
                    return (
                      <Card
                        key={group.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderColor: inSelection ? 'rgba(14,165,233,0.6)' : 'rgba(15,23,42,0.12)',
                          background: inSelection
                            ? 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(37,99,235,0.12) 100%)'
                            : '#fff',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={group.name} sx={{ fontWeight: 700 }} />
                            <Typography variant="caption" color="text.secondary">
                              {groupIds.length} accounts
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setExpandedGroupId(expandedGroupId === group.id ? '' : group.id)}
                            >
                              {expandedGroupId === group.id ? 'Hide' : 'View'}
                            </Button>
                            <Button
                              size="small"
                              variant={inSelection ? 'outlined' : 'contained'}
                              onClick={() => addAccountsToSelection(groupIds)}
                              disabled={!groupIds.length}
                            >
                              {inSelection ? 'Added' : 'Add group'}
                            </Button>
                          </Stack>
                        </Stack>
                        <Collapse in={expandedGroupId === group.id} unmountOnExit>
                          <Stack spacing={0.75} sx={{ mt: 1 }}>
                            {groupIds.map((id) => {
                              const account = accounts.find((a) => a.id === id);
                              if (!account) return null;
                              return (
                                <Stack key={id} direction="row" spacing={1} alignItems="center">
                                  <Checkbox
                                    size="small"
                                    checked={formData.selectedAccounts?.includes(id)}
                                    onChange={(e) => {
                                      const updated = e.target.checked
                                        ? [...(formData.selectedAccounts || []), id]
                                        : formData.selectedAccounts?.filter((item) => item !== id);
                                      setFormData({ ...formData, selectedAccounts: updated });
                                    }}
                                  />
                                  <Stack>
                                    <Typography variant="body2" fontWeight={700}>
                                      {account.label || account.fromEmail || account.username || account.id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {account.fromEmail || account.username || ''}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Collapse>
                      </Card>
                    );
                  })}
                </Stack>
              )}
              <Divider />
              <Grid container spacing={1} sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
                {filteredAccounts.map((account) => {
                  const isSelected = formData.selectedAccounts?.includes(account.id);
                  return (
                    <Grid item xs={12} sm={6} key={account.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          borderColor: isSelected ? 'rgba(14,165,233,0.8)' : 'rgba(15,23,42,0.12)',
                          background: isSelected
                            ? 'linear-gradient(135deg, rgba(14,165,233,0.12) 0%, rgba(37,99,235,0.16) 100%)'
                            : '#fff',
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: isSelected ? '#0ea5e9' : 'rgba(15,23,42,0.08)',
                                color: isSelected ? '#fff' : 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                              }}
                            >
                              {(account.label || account.fromEmail || account.username || 'A')
                                .trim()
                                .slice(0, 1)
                                .toUpperCase()}
                            </Box>
                            <Stack>
                              <Typography variant="body2" fontWeight={800}>
                                {account.label || account.fromEmail || account.username || account.id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {account.fromEmail || account.username || ''}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...(formData.selectedAccounts || []), account.id]
                                : formData.selectedAccounts?.filter((id) => id !== account.id);
                              setFormData({ ...formData, selectedAccounts: updated });
                            }}
                          />
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
                {filteredAccounts.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      No accounts match this search.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          )}

          {wizardStep === 2 && (
            <Stack spacing={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.25, md: 1.5 },
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(15,23,42,0.03) 0%, rgba(14,165,233,0.08) 100%)',
                }}
              >
                <Stack spacing={0.75}>
                  <Typography fontWeight={800} variant="h6">
                    Import Recipients
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose the source carefully and verify the loaded rows before moving to anti-spam and scheduling.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`${importRecipientCount} rows loaded`} />
                    <Chip size="small" label={`${importRowsWithEmail} with email`} sx={{ bgcolor: 'rgba(16,185,129,0.12)' }} />
                    <Chip size="small" label={formData.useMcRange ? 'Automation mode' : 'Manual import mode'} sx={{ bgcolor: 'rgba(14,165,233,0.12)' }} />
                  </Stack>
                </Stack>
              </Paper>

              <Box>
                <Typography fontWeight={800} variant="subtitle1" gutterBottom>
                  Import Recipients
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Switch between direct file import and background carrier discovery.
                </Typography>
              </Box>
              
              {/* Import Method Selection Tabs */}
              <Box sx={{ 
                p: 0.5,
                borderRadius: 3,
                bgcolor: 'rgba(15,23,42,0.04)',
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiTabs-root': { minHeight: 52 }
              }}>
                <Tabs 
                  value={formData.useMcRange ? 1 : 0} 
                  onChange={(e, val) => setFormData({ ...formData, useMcRange: val === 1 })}
                  variant="fullWidth"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      minHeight: 52,
                      borderRadius: 2.5,
                      mx: 0.5,
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      '&.Mui-selected': {
                        color: '#0ea5e9',
                        bgcolor: '#fff',
                        boxShadow: '0 10px 24px rgba(15,23,42,0.08)',
                      }
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none',
                    }
                  }}
                >
                  <Tab 
                    icon={<Upload sx={{ fontSize: 20 }} />} 
                    iconPosition="start"
                    label="Upload File" 
                  />
                  <Tab 
                    icon={<Rocket sx={{ fontSize: 20 }} />} 
                    iconPosition="start"
                    label="MC Range Auto-Search" 
                  />
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ minHeight: 260 }}>
                {!formData.useMcRange ? (
                  <Stack spacing={1.5}>
                    <Alert 
                      severity="info" 
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiAlert-icon': { fontSize: 24 },
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(14,165,233,0.08) 100%)',
                        py: 0.25,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        📁 Upload Recipients File
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                        Upload CSV or Excel file with columns like email, name, company, truck type, state, and custom merge fields.
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label="CSV / XLSX" />
                        <Chip size="small" label="Merge tags ready" sx={{ bgcolor: 'rgba(16,185,129,0.12)' }} />
                        <Chip size="small" label="Preview before send" sx={{ bgcolor: 'rgba(14,165,233,0.12)' }} />
                      </Stack>
                    </Alert>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Button 
                          variant="outlined" 
                          component="label" 
                          fullWidth
                          startIcon={<Upload />}
                          sx={{ 
                            py: 1,
                            minHeight: 62,
                            borderRadius: 2.5,
                            borderWidth: 2,
                            borderStyle: 'dashed',
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            '&:hover': {
                              borderWidth: 2,
                              borderColor: '#0ea5e9',
                              background: 'rgba(14,165,233,0.05)',
                            }
                          }}
                        >
                          <Stack alignItems="flex-start" spacing={0.25}>
                            <Typography fontWeight={800} variant="body2">Upload File</Typography>
                            <Typography variant="caption" color="text.secondary">CSV or Excel</Typography>
                          </Stack>
                          <input
                            type="file"
                            hidden
                            accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleRecipientFileUpload}
                          />
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Button 
                          variant="outlined" 
                          fullWidth
                          startIcon={<GpsFixed />} 
                          onClick={handleLoadCarrierExport}
                          sx={{ 
                            py: 1,
                            minHeight: 62,
                            borderRadius: 2.5,
                            borderWidth: 2,
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                          }}
                        >
                          <Stack alignItems="flex-start" spacing={0.25}>
                            <Typography fontWeight={800} variant="body2">Load Search Export</Typography>
                            <Typography variant="caption" color="text.secondary">Latest carrier data</Typography>
                          </Stack>
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Button 
                          variant="outlined" 
                          fullWidth
                          startIcon={<Download />} 
                          onClick={downloadRecipientTemplate}
                          sx={{ 
                            py: 1,
                            minHeight: 62,
                            borderRadius: 2.5,
                            borderWidth: 2,
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                          }}
                        >
                          <Stack alignItems="flex-start" spacing={0.25}>
                            <Typography fontWeight={800} variant="body2">Download Template</Typography>
                            <Typography variant="caption" color="text.secondary">Correct format</Typography>
                          </Stack>
                        </Button>
                      </Grid>
                    </Grid>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        background: 'linear-gradient(135deg, rgba(15,23,42,0.03) 0%, rgba(14,165,233,0.06) 100%)',
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Typography fontWeight={800}>Import Snapshot</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                              <Typography variant="caption" color="text.secondary">Rows</Typography>
                              <Typography variant="h6" fontWeight={800}>{importRecipientCount}</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                              <Typography variant="caption" color="text.secondary">With Email</Typography>
                              <Typography variant="h6" fontWeight={800}>{importRowsWithEmail}</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, height: '100%' }}>
                              <Typography variant="caption" color="text.secondary">Preview Columns</Typography>
                              <Typography variant="body2" fontWeight={700}>
                                {importPreviewKeys.length ? importPreviewKeys.join(', ') : 'Waiting for import'}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>

                    {recipientFileError && (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {recipientFileError}
                      </Alert>
                    )}
                    
                    {carrierImportNotice && (
                      <Alert severity="success" sx={{ borderRadius: 2 }}>
                        {carrierImportNotice}
                      </Alert>
                    )}
                    
                    {formData.recipientFileName && (
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1.25, 
                          borderRadius: 2.5,
                          background: 'rgba(34,197,94,0.05)',
                          borderColor: 'rgba(34,197,94,0.3)'
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                          <Stack direction="row" spacing={2} alignItems="center">
                            <CheckCircle sx={{ color: '#22c55e', fontSize: 28 }} />
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight={800}>
                              {formData.recipientFileName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {importRecipientCount} rows loaded, {importRowsWithEmail} ready with email
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip size="small" color="success" label="Import ready" />
                        </Stack>
                      </Paper>
                    )}
                    
                    {formData.recipientRows.length > 0 && (
                      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', background: 'rgba(15,23,42,0.03)' }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography fontWeight={800}>Recipient Preview</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Showing first 5 rows and first {importPreviewKeys.length} columns
                            </Typography>
                          </Stack>
                        </Box>
                        <TableContainer 
                          component={Box}
                          sx={{ 
                            maxHeight: 180,
                            '& .MuiTableCell-head': {
                              fontWeight: 700,
                              background: 'rgba(15,23,42,0.05)'
                            }
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                {importPreviewKeys.map((key) => (
                                  <TableCell key={key}>{key}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {formData.recipientRows.slice(0, 5).map((row, idx) => (
                                <TableRow key={idx} hover>
                                  {importPreviewKeys.map((key) => (
                                    <TableCell key={key}>{String(row[key] ?? '') || '-'}</TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <Alert 
                      severity="success" 
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(14,165,233,0.1) 100%)',
                        '& .MuiAlert-icon': { fontSize: 28 }
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} gutterBottom sx={{ fontSize: '1rem' }}>
                        🚀 Automatic Carrier Discovery & Outreach
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Backend will automatically search carriers in your MC range and send personalized emails with their details.
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label="Runs in Background" size="small" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#16a34a', fontWeight: 600 }} />
                        <Chip label="Works After Logout" size="small" sx={{ bgcolor: 'rgba(14,165,233,0.15)', color: '#0284c7', fontWeight: 600 }} />
                        <Chip label="Auto Merge Tags" size="small" sx={{ bgcolor: 'rgba(168,85,247,0.15)', color: '#9333ea', fontWeight: 600 }} />
                      </Stack>
                    </Alert>
                    
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 3,
                        borderWidth: 2,
                        borderColor: 'rgba(14,165,233,0.3)',
                        background: 'linear-gradient(135deg, rgba(14,165,233,0.03) 0%, rgba(37,99,235,0.05) 100%)',
                        overflow: 'hidden'
                      }}
                    >
                      <Box 
                        sx={{ 
                          p: 2, 
                          background: 'linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%)',
                          color: '#fff'
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <GpsFixed sx={{ fontSize: 24 }} />
                          <Typography variant="h6" fontWeight={700}>
                            MC Number Range
                          </Typography>
                        </Stack>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Starting MC Number"
                              placeholder="e.g., 1000000"
                              value={formData.mcStartNumber}
                              onChange={(e) => setFormData({ ...formData, mcStartNumber: e.target.value })}
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><Typography fontWeight={700} color="primary">MC-</Typography></InputAdornment>,
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  fontSize: '1.1rem',
                                  '&:hover fieldset': {
                                    borderColor: '#0ea5e9',
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Ending MC Number"
                              placeholder="e.g., 1050000"
                              value={formData.mcEndNumber}
                              onChange={(e) => setFormData({ ...formData, mcEndNumber: e.target.value })}
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><Typography fontWeight={700} color="primary">MC-</Typography></InputAdornment>,
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  fontSize: '1.1rem',
                                  '&:hover fieldset': {
                                    borderColor: '#0ea5e9',
                                  }
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                        
                        {formData.mcStartNumber && formData.mcEndNumber && (
                          <Alert 
                            severity="info" 
                            icon={<Analytics />}
                            sx={{ 
                              mt: 3,
                              borderRadius: 2,
                              background: 'rgba(59,130,246,0.1)',
                              '& .MuiAlert-icon': { fontSize: 28 }
                            }}
                          >
                            <Typography variant="body2">
                              Search Range: <strong style={{ fontSize: '1.1rem', color: '#2563eb' }}>
                                {(parseInt(formData.mcEndNumber) - parseInt(formData.mcStartNumber) + 1).toLocaleString()}
                              </strong> MC numbers will be automatically searched
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>

                    <Card 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 3,
                        borderWidth: 2,
                        borderColor: 'rgba(15,23,42,0.15)',
                        background: 'rgba(15,23,42,0.02)'
                      }}
                    >
                      <Box 
                        sx={{ 
                          p: 2, 
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          background: 'rgba(15,23,42,0.03)'
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <CheckCircle sx={{ fontSize: 24, color: '#6366f1' }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              Target Carriers
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Select which types of carriers to include in the campaign
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderWidth: 2,
                                borderColor: formData.targetActiveCarriers ? '#22c55e' : 'divider',
                                background: formData.targetActiveCarriers ? 'rgba(34,197,94,0.05)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#22c55e',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(34,197,94,0.15)'
                                }
                              }}
                              onClick={() => setFormData({ ...formData, targetActiveCarriers: !formData.targetActiveCarriers })}
                            >
                              <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: formData.targetActiveCarriers ? '#22c55e' : 'rgba(34,197,94,0.1)',
                                  }}
                                >
                                  <CheckCircle sx={{ fontSize: 28, color: formData.targetActiveCarriers ? '#fff' : '#22c55e' }} />
                                </Box>
                                <Box>
                                  <Typography variant="body1" fontWeight={700}>
                                    Active Carriers
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Currently operating
                                  </Typography>
                                </Box>
                                <Checkbox
                                  checked={formData.targetActiveCarriers}
                                  sx={{ mt: 'auto' }}
                                />
                              </Stack>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} sm={4}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderWidth: 2,
                                borderColor: formData.targetInactiveCarriers ? '#f59e0b' : 'divider',
                                background: formData.targetInactiveCarriers ? 'rgba(245,158,11,0.05)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#f59e0b',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(245,158,11,0.15)'
                                }
                              }}
                              onClick={() => setFormData({ ...formData, targetInactiveCarriers: !formData.targetInactiveCarriers })}
                            >
                              <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: formData.targetInactiveCarriers ? '#f59e0b' : 'rgba(245,158,11,0.1)',
                                  }}
                                >
                                  <PendingActions sx={{ fontSize: 28, color: formData.targetInactiveCarriers ? '#fff' : '#f59e0b' }} />
                                </Box>
                                <Box>
                                  <Typography variant="body1" fontWeight={700}>
                                    Non-Active
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Not currently active
                                  </Typography>
                                </Box>
                                <Checkbox
                                  checked={formData.targetInactiveCarriers}
                                  sx={{ mt: 'auto' }}
                                />
                              </Stack>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} sm={4}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderWidth: 2,
                                borderColor: formData.targetBrokers ? '#6366f1' : 'divider',
                                background: formData.targetBrokers ? 'rgba(99,102,241,0.05)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#6366f1',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(99,102,241,0.15)'
                                }
                              }}
                              onClick={() => setFormData({ ...formData, targetBrokers: !formData.targetBrokers })}
                            >
                              <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: formData.targetBrokers ? '#6366f1' : 'rgba(99,102,241,0.1)',
                                  }}
                                >
                                  <Rocket sx={{ fontSize: 28, color: formData.targetBrokers ? '#fff' : '#6366f1' }} />
                                </Box>
                                <Box>
                                  <Typography variant="body1" fontWeight={700}>
                                    Brokers
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Freight brokers
                                  </Typography>
                                </Box>
                                <Checkbox
                                  checked={formData.targetBrokers}
                                  sx={{ mt: 'auto' }}
                                />
                              </Stack>
                            </Paper>
                          </Grid>
                        </Grid>
                        
                        {!formData.targetActiveCarriers && !formData.targetInactiveCarriers && !formData.targetBrokers && (
                          <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                              ⚠️ Please select at least one carrier type to target
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Stack>
                )}
              </Box>
            </Stack>
          )}

          {wizardStep === 3 && (
            <Stack spacing={1.5}>
              {/* Compact Header */}
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Box>
                  <Typography fontWeight={700} variant="subtitle1">
                    📧 Deliverability Settings
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Land in inbox with professional sending practices
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Chip label="Human delays" size="small" sx={{ bgcolor: 'rgba(34,197,94,0.12)', fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                  <Chip label="Rotation" size="small" sx={{ bgcolor: 'rgba(14,165,233,0.12)', fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                  <Chip label="Limits" size="small" sx={{ bgcolor: 'rgba(168,85,247,0.12)', fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                </Stack>
              </Stack>

              {/* User Guide */}
              <Alert severity="info" sx={{ py: 1, '& .MuiAlert-icon': { fontSize: 18 } }}>
                <Typography variant="body2" fontWeight={700} fontSize="0.8rem" gutterBottom>
                  📘 User Guide: Anti-Spam Settings Explained
                </Typography>
                <Typography variant="caption" fontSize="0.72rem" color="text.secondary" component="div">
                  These settings protect your emails from spam folders. When you send too many emails too fast, 
                  email providers (Gmail, Outlook, etc.) mark you as spam. Use these controls to send like a real person:
                </Typography>
                <Box sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid rgba(33,150,243,0.3)' }}>
                  <Typography variant="caption" fontSize="0.72rem" display="block" sx={{ mb: 0.5 }}>
                    <strong>⏱️ Timing:</strong> Add delays between emails (e.g., wait 30-90 seconds). Real humans don't send 1000 emails per minute!
                  </Typography>
                  <Typography variant="caption" fontSize="0.72rem" display="block" sx={{ mb: 0.5 }}>
                    <strong>🎯 Limits:</strong> Cap hourly volume (e.g., max 40/hour). Prevents your account from being flagged for bulk sending.
                  </Typography>
                  <Typography variant="caption" fontSize="0.72rem" display="block">
                    <strong>🔄 Rotation:</strong> Switch between accounts & templates. Avoids repetitive patterns that spam filters detect.
                  </Typography>
                </Box>
              </Alert>

              <Grid container spacing={1}>
                {/* Timing */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 0.75, background: '#0ea5e9', color: '#fff' }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <AccessTime sx={{ fontSize: 16 }} />
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={700} fontSize="0.8rem">⏱️ Timing Controls</Typography>
                          <Typography variant="caption" fontSize="0.65rem" sx={{ opacity: 0.9 }}>
                            How long to wait between sending each email
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-icon': { fontSize: 14 } }}>
                          <Typography variant="caption" fontSize="0.7rem">
                            <strong>Why delays?</strong> Sending 100 emails in 10 seconds looks automated and gets flagged. 
                            Adding 30-90 second gaps makes it appear natural.
                          </Typography>
                        </Alert>

                        <FormControl fullWidth size="small">
                          <InputLabel>Delay Mode</InputLabel>
                          <Select
                            value={formData.delayMode}
                            onChange={(e) => setFormData({ ...formData, delayMode: e.target.value })}
                            label="Delay Mode"
                          >
                            <MenuItem value="fixed">
                              <Box>
                                <Typography fontSize="0.8rem">Fixed - Same every time</Typography>
                                <Typography variant="caption" fontSize="0.65rem" color="text.secondary">
                                  Example: Always wait exactly 60 seconds (predictable pattern)
                                </Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="range">
                              <Box>
                                <Typography fontSize="0.8rem">Random - Varies ✓ Recommended</Typography>
                                <Typography variant="caption" fontSize="0.65rem" color="text.secondary">
                                  Example: Wait 30-90 seconds randomly (looks human, unpredictable)
                                </Typography>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {formData.delayMode === 'range' ? (
                          <>
                            <Typography variant="caption" fontSize="0.72rem" color="text.secondary">
                              Set min/max range. System will pick random delay between these values for each email.
                            </Typography>
                            <Grid container spacing={0.75}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Minimum"
                                  value={formData.minDelaySec}
                                  onChange={(e) => setFormData({ ...formData, minDelaySec: e.target.value })}
                                  InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>sec</InputAdornment> }}
                                  helperText="Shortest wait (30+ recommended)"
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Maximum"
                                  value={formData.maxDelaySec}
                                  onChange={(e) => setFormData({ ...formData, maxDelaySec: e.target.value })}
                                  InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>sec</InputAdornment> }}
                                  helperText="Longest wait (90+ recommended)"
                                />
                              </Grid>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" fontSize="0.72rem" color="text.secondary">
                              Fixed delay means every email waits the exact same amount of time (less human-like).
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Delay Duration"
                              value={formData.delaySeconds}
                              onChange={(e) => setFormData({ ...formData, delaySeconds: e.target.value })}
                              InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>sec</InputAdornment> }}
                              helperText="Constant wait time (60+ seconds recommended)"
                            />
                          </>
                        )}

                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Jitter (Extra Randomness)"
                          value={formData.jitterSec}
                          onChange={(e) => setFormData({ ...formData, jitterSec: e.target.value })}
                          InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>±sec</InputAdornment> }}
                          helperText="Adds ±N seconds variation (e.g., 5 = adds/subtracts up to 5s)"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.enforceMinDelay}
                              onChange={(e) => setFormData({ ...formData, enforceMinDelay: e.target.checked })}
                            />
                          }
                          label={
                            <Typography variant="caption" fontSize="0.7rem">
                              Safety Lock: Never allow delays below 10 seconds
                            </Typography>
                          }
                        />

                        <Paper sx={{ p: 1, bgcolor: 'rgba(14,165,233,0.08)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.7rem" fontWeight={600} color="primary" gutterBottom display="block">
                            💡 Example Best Setup:
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" color="text.secondary">
                            Mode: Random (30 min - 90 max) + 5 jitter = Each email waits 25-95 seconds randomly. Looks completely natural!
                          </Typography>
                        </Paper>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>

                {/* Volume Limits */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 0.75, background: '#f59e0b', color: '#fff' }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <GpsFixed sx={{ fontSize: 16 }} />
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={700} fontSize="0.8rem">🎯 Volume Limits</Typography>
                          <Typography variant="caption" fontSize="0.65rem" sx={{ opacity: 0.9 }}>
                            Maximum emails allowed per hour & auto-stop rules
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-icon': { fontSize: 14 } }}>
                          <Typography variant="caption" fontSize="0.7rem">
                            <strong>Why limits?</strong> Sending 500+ emails/hour from one account triggers spam filters. 
                            Keep it under 40-50/hour per account to stay safe.
                          </Typography>
                        </Alert>

                        <Typography variant="caption" fontSize="0.72rem" fontWeight={600} color="text.secondary">
                          Hourly Caps (Per Account)
                        </Typography>
                        <Grid container spacing={0.75}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Max Per Hour"
                              value={formData.maxPerAccountPerHour}
                              onChange={(e) => setFormData({ ...formData, maxPerAccountPerHour: e.target.value })}
                              helperText="Max emails each account can send/hr"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Stop After Fails"
                              value={formData.maxFailures}
                              onChange={(e) => setFormData({ ...formData, maxFailures: e.target.value })}
                              helperText="Auto-pause if N fails in a row"
                            />
                          </Grid>
                        </Grid>

                        <Divider>
                          <Typography variant="caption" fontSize="0.65rem">
                            Batch Pause (Take Breaks Like Humans)
                          </Typography>
                        </Divider>

                        <Typography variant="caption" fontSize="0.72rem" color="text.secondary">
                          After sending a batch of emails, take a break. Example: Send 50 emails → Pause 2 minutes → Resume.
                        </Typography>
                        <Grid container spacing={0.75}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Pause Every"
                              value={formData.batchPauseEvery}
                              onChange={(e) => setFormData({ ...formData, batchPauseEvery: e.target.value })}
                              helperText="Number of emails before pause"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Pause For"
                              value={formData.batchPauseSec}
                              onChange={(e) => setFormData({ ...formData, batchPauseSec: e.target.value })}
                              InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>sec</InputAdornment> }}
                              helperText="How long to pause (120 = 2min)"
                            />
                          </Grid>
                        </Grid>

                        <Paper sx={{ p: 1, bgcolor: 'rgba(245,158,11,0.08)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.7rem" fontWeight={600} color="warning.main" gutterBottom display="block">
                            ⚡ Safe Professional Settings:
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" color="text.secondary">
                            • Max: 40 emails/hour per account<br/>
                            • Stop: After 5 consecutive failures<br/>
                            • Pause: Every 50 emails for 120 seconds (2 minutes)
                          </Typography>
                        </Paper>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>

                {/* Rotation */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 0.75, background: '#6366f1', color: '#fff' }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Rocket sx={{ fontSize: 16 }} />
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={700} fontSize="0.8rem">🔄 Pattern Randomization</Typography>
                          <Typography variant="caption" fontSize="0.65rem" sx={{ opacity: 0.9 }}>
                            Shuffle accounts & templates to avoid repetitive patterns
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-icon': { fontSize: 14 } }}>
                          <Typography variant="caption" fontSize="0.7rem">
                            <strong>Why randomize?</strong> If you always use Account #1 first and send the same template in same order, 
                            spam filters detect the pattern. Rotation makes each campaign look unique.
                          </Typography>
                        </Alert>

                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1.25, 
                                borderRadius: 1,
                                borderColor: formData.rotateAccounts ? '#6366f1' : 'divider',
                                background: formData.rotateAccounts ? 'rgba(99,102,241,0.08)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: '#6366f1', background: 'rgba(99,102,241,0.05)' }
                              }}
                              onClick={() => setFormData({ ...formData, rotateAccounts: !formData.rotateAccounts })}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Checkbox size="small" checked={formData.rotateAccounts} sx={{ mt: -0.5 }} />
                                <Box flex={1}>
                                  <Typography variant="body2" fontWeight={600} fontSize="0.8rem" gutterBottom>
                                    Rotate Email Accounts
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block" sx={{ mb: 0.5 }}>
                                    <strong>What it does:</strong> Switches between your email accounts automatically
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block">
                                    <strong>Why important:</strong> No single account sends too many emails. Spreads load evenly.
                                    Example: Have 3 accounts? Each sends 33% instead of one sending 100%.
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1.25, 
                                borderRadius: 1,
                                borderColor: formData.shuffleTemplates ? '#a855f7' : 'divider',
                                background: formData.shuffleTemplates ? 'rgba(168,85,247,0.08)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: '#a855f7', background: 'rgba(168,85,247,0.05)' }
                              }}
                              onClick={() => setFormData({ ...formData, shuffleTemplates: !formData.shuffleTemplates })}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Checkbox size="small" checked={formData.shuffleTemplates} sx={{ mt: -0.5 }} />
                                <Box flex={1}>
                                  <Typography variant="body2" fontWeight={600} fontSize="0.8rem" gutterBottom>
                                    Shuffle Email Templates
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block" sx={{ mb: 0.5 }}>
                                    <strong>What it does:</strong> Randomly picks templates in different order each time
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block">
                                    <strong>Why important:</strong> Avoids sending identical emails in same sequence repeatedly.
                                    Makes each recipient's email appear personalized and unique.
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>

                {/* Best Practices */}
                <Grid item xs={12}>
                  <Alert 
                    severity="success"
                    sx={{ py: 1, borderRadius: 1.5, '& .MuiAlert-icon': { fontSize: 18 } }}
                  >
                    <Typography variant="body2" fontWeight={700} fontSize="0.8rem" gutterBottom>
                      ✅ Professional Email Best Practices (Follow All to Stay Out of Spam)
                    </Typography>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.72rem" display="block" fontWeight={600} gutterBottom>
                            📧 Email Authentication
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" display="block" color="text.secondary">
                            <strong>SPF/DKIM:</strong> Setup domain authentication in your email provider settings. 
                            Without this, emails are marked "unverified" and flagged as spam.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.72rem" display="block" fontWeight={600} gutterBottom>
                            🔥 Account Warmup
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" display="block" color="text.secondary">
                            <strong>Start Slow:</strong> New email accounts should send 10-20 emails/day for first week, 
                            then gradually increase. Sudden high volume = instant spam flag.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.72rem" display="block" fontWeight={600} gutterBottom>
                            👤 Personalization
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" display="block" color="text.secondary">
                            <strong>Use Merge Tags:</strong> Include {`{{name}}, {{company}}, {{mc_number}}`} in templates. 
                            Generic "Dear Sir/Madam" emails are spam. Personalized = higher trust.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.72rem" display="block" fontWeight={600} gutterBottom>
                            🚫 Spam Words
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" display="block" color="text.secondary">
                            <strong>Avoid:</strong> "FREE", "CLICK NOW", "ACT FAST", "100% GUARANTEE", "MAKE MONEY FAST". 
                            Use professional language only.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.72rem" display="block" fontWeight={600} gutterBottom>
                            📊 Monitor Bounces
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" display="block" color="text.secondary">
                            <strong>Remove Bad Emails:</strong> If emails bounce (invalid addresses), remove them immediately. 
                            High bounce rate (5%) destroys your sender reputation.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" fontSize="0.72rem" display="block" fontWeight={600} gutterBottom>
                            🔗 Unsubscribe Link
                          </Typography>
                          <Typography variant="caption" fontSize="0.7rem" display="block" color="text.secondary">
                            <strong>Always Include:</strong> Add clear unsubscribe option in every email footer. 
                            Required by law (CAN-SPAM) and keeps spam complaints low.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Alert>
                </Grid>
              </Grid>
            </Stack>
          )}

          {wizardStep === 4 && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Schedule Date & Time"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="EST">Eastern Standard</MenuItem>
                  <MenuItem value="CST">Central Standard</MenuItem>
                  <MenuItem value="PST">Pacific Standard</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Sending Frequency</InputLabel>
                <Select
                  value={formData.sendingFrequency}
                  onChange={(e) => setFormData({ ...formData, sendingFrequency: e.target.value })}
                  label="Sending Frequency"
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {wizardStep === 5 && (
            <Stack spacing={2.5}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(15,23,42,0.03) 0%, rgba(14,165,233,0.08) 100%)',
                  borderColor: 'rgba(14,165,233,0.16)'
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Review Before Launch
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Validate campaign structure, sending controls, and tracking before you save or schedule it.
                      </Typography>
                    </Box>
                    <Chip
                      label={`${reviewReadyCount}/${reviewChecks.length} checks ready`}
                      color={reviewReadyCount === reviewChecks.length ? 'success' : 'warning'}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Grid container spacing={1.25}>
                    {reviewSummaryCards.map((card) => (
                      <Grid item xs={6} md={3} key={card.label}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            background: card.tone,
                            borderColor: card.border,
                            height: '100%'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {card.label}
                          </Typography>
                          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.35 }}>
                            {card.value}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800}>
                          Campaign Overview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Core campaign setup and imported data snapshot.
                        </Typography>
                      </Box>

                      <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary">Campaign Name</Typography>
                            <Typography fontWeight={700}>{formData.name || 'Untitled campaign'}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary">Recipient Source</Typography>
                            <Typography fontWeight={700}>{formData.recipientFileName || 'Manual / pending import'}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary">Schedule</Typography>
                            <Typography fontWeight={700}>{reviewScheduleLabel}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary">Timezone</Typography>
                            <Typography fontWeight={700}>{formData.timezone || 'UTC'}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <Divider />

                      <Grid container spacing={1.25}>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
                            <Typography variant="body2" fontWeight={800} gutterBottom>
                              Delivery Controls
                            </Typography>
                            <Stack spacing={1}>
                              {reviewDeliveryItems.map((item) => (
                                <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                  <Typography variant="body2" fontWeight={700} textAlign="right">{item.value}</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
                            <Typography variant="body2" fontWeight={800} gutterBottom>
                              Tracking & Behavior
                            </Typography>
                            <Stack spacing={1}>
                              {reviewBehaviorItems.map((item) => (
                                <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                  <Chip
                                    size="small"
                                    label={item.value}
                                    color={item.value === 'Enabled' ? 'success' : 'default'}
                                    variant={item.value === 'Enabled' ? 'filled' : 'outlined'}
                                    sx={{ fontWeight: 700, minWidth: 86 }}
                                  />
                                </Stack>
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                        Launch Readiness
                      </Typography>
                      <Stack spacing={1}>
                        {reviewChecks.map((item) => (
                          <Paper
                            key={item.label}
                            variant="outlined"
                            sx={{
                              p: 1.25,
                              borderRadius: 2,
                              borderColor: item.ok ? 'rgba(34,197,94,0.28)' : 'rgba(245,158,11,0.28)',
                              background: item.ok ? 'rgba(34,197,94,0.06)' : 'rgba(245,158,11,0.06)'
                            }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: item.ok ? 'rgba(34,197,94,0.14)' : 'rgba(245,158,11,0.16)',
                                  color: item.ok ? '#15803d' : '#b45309',
                                  flexShrink: 0
                                }}
                              >
                                {item.ok ? <CheckCircle sx={{ fontSize: 18 }} /> : <PendingActions sx={{ fontSize: 18 }} />}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700}>
                                  {item.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.helper}
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>

                    <Alert
                      severity={reviewNotReady.length === 0 ? 'success' : 'warning'}
                      sx={{ borderRadius: 3 }}
                    >
                      <Typography variant="body2" fontWeight={700} gutterBottom>
                        {reviewNotReady.length === 0 ? 'Campaign is ready to save or schedule.' : 'Complete the remaining setup before launch.'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reviewNotReady.length === 0
                          ? 'Templates, accounts, import data, and schedule settings are all in place.'
                          : `Still pending: ${reviewNotReady.map((item) => item.label).join(', ')}.`}
                      </Typography>
                    </Alert>

                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.trackOpens}
                            onChange={(e) => setFormData({ ...formData, trackOpens: e.target.checked })}
                          />
                        }
                        label="Track Opens"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.trackClicks}
                            onChange={(e) => setFormData({ ...formData, trackClicks: e.target.checked })}
                          />
                        }
                        label="Track Clicks"
                      />
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setWizardOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            disabled={wizardStep === 0}
            onClick={handleWizardPrev}
            variant="outlined"
            sx={{ borderColor: 'rgba(15,23,42,0.25)' }}
          >
            Back
          </Button>
          {wizardStep < wizardSteps.length - 1 ? (
            <Button
              onClick={handleWizardNext}
              variant="contained"
              disabled={!formData.name || (formData.selectedTemplates || []).length === 0}
              sx={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                boxShadow: '0 10px 20px rgba(37,99,235,0.25)',
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCreatePlan}
              variant="contained"
              startIcon={<Rocket />}
              sx={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                boxShadow: '0 12px 24px rgba(37,99,235,0.35)',
              }}
            >
              {editingPlan ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ====== PREVIEW DIALOG ====== */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Campaign Preview</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                <Typography fontWeight={700} mb={1}>Email Preview</Typography>
                <Box sx={{
                  p: 2,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {previewTemplate?.body ? (
                    <Box
                      sx={{ width: '100%' }}
                      dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
                    />
                  ) : (
                    <Typography color="text.secondary">No template selected for this campaign.</Typography>
                  )}
                </Box>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ====== ANALYTICS DIALOG ====== */}
      <Dialog open={analyticsDialog} onClose={() => setAnalyticsDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>Campaign Analytics</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            (() => {
              const totalRecipients = selectedStats.totalRecipients || 0;
              const opens = selectedStats.opens || 0;
              const clicks = selectedStats.clicks || 0;
              const openRate = totalRecipients ? Math.round((opens / totalRecipients) * 100) : 0;
              const clickRate = totalRecipients ? Math.round((clicks / totalRecipients) * 100) : 0;
              return (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    {!totalRecipients ? (
                      <Alert severity="info">No analytics yet. Launch the campaign to start tracking opens.</Alert>
                    ) : null}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, background: '#e3f2fd' }}>
                      <Typography variant="caption" color="text.secondary">Open Rate</Typography>
                      <Typography fontWeight={700} variant="h6">{openRate}%</Typography>
                      <LinearProgress variant="determinate" value={openRate} sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, background: '#e8f5e9' }}>
                      <Typography variant="caption" color="text.secondary">Click Rate</Typography>
                      <Typography fontWeight={700} variant="h6">{clickRate}%</Typography>
                      <LinearProgress variant="determinate" value={clickRate} sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Sent</Typography>
                          <Typography fontWeight={700}>{totalRecipients}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Opens</Typography>
                          <Typography fontWeight={700}>{opens}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Clicks</Typography>
                          <Typography fontWeight={700}>{clicks}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Last open</Typography>
                          <Typography fontWeight={700}>{selectedStats.lastOpenedAt || '—'}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              );
            })()
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failedDialogOpen} onClose={() => setFailedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Failed Recipients / Errors</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(failedDialogPlan && (planStats[failedDialogPlan.id]?.failedRecipients || []).length)
              ? (planStats[failedDialogPlan.id]?.failedRecipients || []).map((item, idx) => (
                <Paper key={`${item.email}-${idx}`} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{item.email}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.error || 'Send failed'}</Typography>
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No failed recipients.</Typography>}
            {(failedDialogPlan && (planStats[failedDialogPlan.id]?.events || []).length)
              ? (
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography fontWeight={700} variant="body2" mb={1}>Recent Events</Typography>
                  <Stack spacing={0.5}>
                    {(planStats[failedDialogPlan.id]?.events || []).slice(-8).reverse().map((event, idx) => (
                      <Typography key={`${event.at || 'evt'}-${idx}`} variant="caption" color="text.secondary">
                        [{event.at || '-'}] {event.message || ''}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFailedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={trackingDialogOpen} onClose={() => setTrackingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tracking Issues</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(trackingDialogPlan && (planStats[trackingDialogPlan.id]?.trackingWarnings || []).length)
              ? (planStats[trackingDialogPlan.id]?.trackingWarnings || []).map((item, idx) => (
                <Paper key={`${item.email}-${idx}`} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{item.email}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.error || 'Tracking error'}</Typography>
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No tracking issues.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTrackingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templatesDialogOpen} onClose={() => setTemplatesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Templates</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(templatesDialogPlan && ((templatesDialogPlan.templates && templatesDialogPlan.templates.length) || (templatesDialogPlan.strategy?.selectedTemplates || []).length))
              ? (templatesDialogPlan.templates && templatesDialogPlan.templates.length
                ? templatesDialogPlan.templates
                : templates.filter((tmpl) => (templatesDialogPlan.strategy?.selectedTemplates || []).includes(tmpl.id))
              ).map((tmpl) => (
                <Paper key={tmpl.id} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{tmpl.name || 'Untitled template'}</Typography>
                  {tmpl.subject ? (
                    <Typography variant="caption" color="text.secondary">{tmpl.subject}</Typography>
                  ) : null}
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No templates selected.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTemplatesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accountsDialogOpen} onClose={() => setAccountsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Accounts</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {(accountsDialogPlan && (accountsDialogPlan.strategy?.selectedAccounts || accountsDialogPlan.selectedAccounts || []).length)
              ? accounts.filter((acc) => (accountsDialogPlan.strategy?.selectedAccounts || accountsDialogPlan.selectedAccounts || []).includes(acc.id)).map((acc) => (
                <Paper key={acc.id} variant="outlined" sx={{ p: 1 }}>
                  <Typography fontWeight={700} variant="body2">{acc.label || acc.fromEmail || acc.username || 'Account'}</Typography>
                  <Typography variant="caption" color="text.secondary">{acc.fromEmail || acc.username}</Typography>
                </Paper>
              ))
              : <Typography variant="body2" color="text.secondary">No accounts selected.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAccountsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignPlanner;
